/**
 * Home Page - Landing page for Subscrypts Discord Bot.
 *
 * Features:
 * - Dramatic centered hero with gradient text
 * - 3D cyber-grid background
 * - Open layout (no wrapping Card)
 * - Responsive mobile hamburger header
 * - Animated FAQ accordion
 * - InfoSidebar integration
 * - SEO structured data preserved
 */

import { useEffect, useState } from 'react';
import { useWeb3 } from '../contexts/Web3Context';
import { Layout, CyberGrid, InfoSidebar } from '../components';
import { INFO_CONTENT } from '../content/infoContent';

// Constants
const BOT_INVITE_URL = 'https://discord.com/api/oauth2/authorize?client_id=1415020026448318514&scope=bot%20applications.commands&permissions=361045773328';
const SUPPORT_URL = 'https://discord.gg/6uYzBUhAj7';
const DOCS_URL = 'https://docs.subscrypts.com/discord-bot';
const WEBSITE_URL = 'https://subscrypts.com';

const FAQ_DATA = [
  {
    question: 'What is the Subscrypts Discord Bot?',
    answer: 'A managed SaaS bot that connects blockchain-based subscription plans on Arbitrum One to Discord server roles. Server owners create on-chain plans, and the bot automatically grants or revokes roles based on real-time subscription status.',
  },
  {
    question: 'How does the Subscrypts Discord Bot work?',
    answer: 'Admins invite the bot, create subscription plans via a web form, and map each plan to a Discord role. Members link their Ethereum wallet using a gasless signature, then subscribe on-chain. The bot monitors the smart contract and syncs roles automatically.',
  },
  {
    question: 'What blockchain network does it use?',
    answer: 'Arbitrum One — a Layer 2 network on Ethereum (Chain ID 42161). Transactions are fast and cost approximately $0.01 in ETH gas fees.',
  },
  {
    question: 'What tokens can members pay with?',
    answer: 'Subscription payments are made in SUBS tokens. Admins can price plans in SUBS or USDC terms — the amount is auto-converted at checkout. A built-in swap page lets members exchange USDC for SUBS directly.',
  },
  {
    question: 'How do I add the bot to my Discord server?',
    answer: 'Click "Add to Discord" on this page, authorize the bot, then follow the setup guide in the #subscrypts-admin channel. The bot creates the necessary channels and roles automatically.',
  },
  {
    question: 'Do members need to share personal information?',
    answer: 'No. Wallet linking uses Sign-In with Ethereum (SIWE) — a gasless cryptographic signature. No passwords, emails, or personal data are collected. The bot only verifies wallet ownership.',
  },
  {
    question: 'What happens when a subscription expires?',
    answer: "The bot's reconciliation engine periodically checks on-chain state. When a subscription's next payment date passes without renewal, the mapped Discord role is automatically revoked. Members can resubscribe at any time to regain access.",
  },
  {
    question: 'Is the Subscrypts Discord Bot free to use?',
    answer: 'Adding the bot and setting up your server is free. The Subscrypts smart contract charges a small commission on subscription payments — configurable per plan by the merchant.',
  },
  {
    question: 'Can I set up the Subscrypts Discord Bot without coding or technical experience?',
    answer: 'Yes. The entire setup is no-code. The bot guides you through each step via buttons in the admin channel, and the plan creation form is a standard web form that you sign with your wallet. No smart contract deployment or programming is required.',
  },
  {
    question: "How is this different from Discord's built-in subscription tiers?",
    answer: "Discord's native monetization requires Nitro and takes a platform cut from revenue. Subscrypts is fully on-chain and non-custodial — subscription payments go directly to your wallet via smart contract, there is no platform intermediary, no fiat requirements, and no KYC for your members.",
  },
  {
    question: "What happens if a member's wallet runs out of SUBS tokens?",
    answer: 'The subscription will not renew and the bot will automatically revoke the associated Discord role once the subscription period expires. Members can resubscribe at any time by topping up their wallet (via the built-in swap page or any DEX) and completing a new on-chain subscription transaction.',
  },
];

const STEPS = [
  {
    number: '1',
    title: 'Invite the Bot',
    description: (
      <>
        Click <strong className="text-accent">Add to Discord</strong> and authorize
        the requested permissions. After the bot is added, continue to the{' '}
        <strong>#subscrypts-admin</strong> channel for further configuration.
      </>
    ),
    bullets: [
      <><strong className="text-white">Subscrypts Global Bot</strong> is added to your server</>,
      <>Admins receive a <strong className="text-white">welcome DM</strong> with setup info</>,
      <><strong className="text-white">#subscrypts-admin</strong> channel is created automatically</>,
    ],
    video: 'https://files.subscrypts.com/temp/InviteBot.mp4',
  },
  {
    number: '2',
    title: 'Apply Baseline',
    description: (
      <>
        <strong className="text-accent">Accept Terms</strong>, then click the{' '}
        <strong>Apply Baseline</strong> button or send{' '}
        <code className="bg-white/5 px-1.5 py-0.5 rounded-md text-accent font-mono text-sm">/subs-admin apply-baseline</code> to
        create the essential items.
      </>
    ),
    bullets: [
      <>Role: Subscrypts Admins</>,
      <>Channels: #connect, #subscribe, #swap</>,
      <>Admin channel: #subscrypts-admin</>,
    ],
    video: 'https://files.subscrypts.com/temp/Step 2 - Accept TOS - Apply Baseline.mp4',
  },
  {
    number: '3',
    title: '(Optional) Starter Template',
    description: (
      <>
        Click <strong className="text-accent">Apply Starter Template</strong> or
        send <code className="bg-white/5 px-1.5 py-0.5 rounded-md text-accent font-mono text-sm">/subs-admin apply-template</code>{' '}
        to create the default premium structure.
      </>
    ),
    bullets: [
      <>Roles: Premium Lite, Premium Ultra</>,
      <>Channels: #premium-lite, #premium-ultra</>,
      <>Funnel: #no-active-subscription</>,
    ],
    video: 'https://files.subscrypts.com/temp/Step 3 - Apply Starter Template.mp4',
  },
  {
    number: '4',
    title: 'Create Plans',
    description: (
      <>
        Click <strong className="text-accent">Create Subscription</strong> in the
        admin message. Fill in the form and sign with your wallet to deploy your
        subscription plans.
      </>
    ),
    bullets: [
      <>Name, Description & duration (e.g., monthly)</>,
      <>Price in <strong className="text-white">SUBS</strong> or <strong className="text-white">USDC</strong></>,
      <>Users always pay in SUBS (auto-converted at checkout)</>,
    ],
    video: 'https://files.subscrypts.com/temp/Step 4 - Create Plan.mp4',
  },
  {
    number: '5',
    title: 'Map Plans to Roles',
    description: (
      <>
        Click <strong className="text-accent">Map Plan &#x2194; Role</strong> or send{' '}
        <code className="bg-white/5 px-1.5 py-0.5 rounded-md text-accent font-mono text-sm">/subs-admin map</code> for each plan
        to connect it to the correct Discord role.
      </>
    ),
    bullets: [
      <>Creates a mapping between the subscription and role</>,
      <>Members with active plans gain roles automatically</>,
      <>Expired subscriptions remove mapped roles</>,
    ],
    video: 'https://files.subscrypts.com/temp/Step 5 - Map Plan Role.mp4',
  },
  {
    number: '6',
    title: 'For Members: Link Wallet',
    description: (
      <>
        Members can link their <strong className="text-accent">Ethereum wallet</strong>{' '}
        in the <strong>#connect</strong> channel. The bot opens a private thread with a{' '}
        <strong>gasless signature</strong> page.
      </>
    ),
    bullets: [
      <>Private thread visible only to user and admins</>,
      <>User signs a message (no gas cost)</>,
      <>Confirmation appears and thread is renamed</>,
    ],
    video: 'https://files.subscrypts.com/temp/Step 6 - Link Wallet.mp4',
  },
  {
    number: '7',
    title: 'For Members: Subscribe',
    description: (
      <>
        In the <strong>#subscribe</strong> channel, members find your subscription link.
        Click to open the subscription page, confirm the transaction, and the bot assigns
        roles automatically.
      </>
    ),
    bullets: [
      <>Navigate to the subscription page</>,
      <>If short on SUBS, swap via #swap or Uniswap</>,
      <>Optional: use USDC fallback (auto-swaps once)</>,
      <>Wait for sync, then enjoy premium access</>,
    ],
    video: 'https://files.subscrypts.com/temp/Step 7 - Subscribe.mp4',
  },
];

const Home = () => {
  useEffect(() => { document.title = 'Subscrypts Discord Bot \u2014 Blockchain Subscriptions for Discord'; }, []);
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const [showInfo, setShowInfo] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { switchNetwork } = useWeb3();

  const handleAddArbitrum = async () => {
    try {
      await switchNetwork();
    } catch (err) {
      console.error('Failed to switch network:', err);
    }
  };

  return (
    <Layout showHeader={false} showFooter={false}>
      <main className="min-h-screen relative">
        {/* 3D Grid Background — fixed to viewport */}
        <div className="fixed inset-0 overflow-hidden pointer-events-none">
          <CyberGrid />
        </div>

        {/* Content */}
        <div className="relative z-10">
          {/* Custom Header */}
          <header className="sticky top-0 z-50 h-20 bg-[rgba(15,23,42,0.6)] backdrop-blur-[16px] border-b border-white/5 px-4 sm:px-6 flex items-center">
            <div className="max-w-6xl mx-auto w-full flex items-center justify-between">
              {/* Brand */}
              <div className="flex items-center gap-3">
                <img src="/Subscrypts-Small.webp" alt="Subscrypts" width="23" height="32" className="h-8 w-auto" />
                <span className="font-display font-bold text-xl text-white">Subscrypts</span>
              </div>

              {/* Desktop nav links */}
              <div className="hidden md:flex items-center gap-2">
                <a
                  href={WEBSITE_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-4 py-2 text-sm rounded-full text-text-muted hover:text-white hover:bg-white/5 border border-transparent transition-all"
                >
                  Website
                </a>
                <a
                  href={SUPPORT_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-4 py-2 text-sm rounded-full text-text-muted hover:text-white hover:bg-white/5 border border-transparent transition-all"
                >
                  Support
                </a>
                <a
                  href={DOCS_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-4 py-2 text-sm rounded-full text-text-muted hover:text-white hover:bg-white/5 border border-transparent transition-all"
                >
                  Docs
                </a>
                <a
                  href={BOT_INVITE_URL}
                  className="px-5 py-2 text-sm rounded-full bg-accent text-slate-950 font-medium hover:bg-accent-hover shadow-glow transition-all"
                >
                  Add to Discord
                </a>
                {/* Info button */}
                <button
                  onClick={() => setShowInfo(true)}
                  className="p-2 rounded-full bg-white/5 border border-white/5 hover:bg-white/10 hover:border-white/10 transition-all text-text-muted hover:text-white"
                  aria-label="Page information"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                      d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </button>
              </div>

              {/* Mobile: CTA + hamburger */}
              <div className="flex md:hidden items-center gap-2">
                <a
                  href={BOT_INVITE_URL}
                  className="px-4 py-2 text-sm rounded-full bg-accent text-slate-950 font-medium hover:bg-accent-hover shadow-glow transition-all"
                >
                  Add to Discord
                </a>
                <button
                  onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                  className="p-2 rounded-full bg-white/5 hover:bg-white/10 transition-colors"
                  aria-label="Toggle menu"
                >
                  <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    {mobileMenuOpen ? (
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    ) : (
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                    )}
                  </svg>
                </button>
              </div>
            </div>

            {/* Mobile menu dropdown */}
            {mobileMenuOpen && (
              <div className="md:hidden absolute top-20 left-0 right-0 bg-[rgba(15,23,42,0.95)] backdrop-blur-[20px] border-b border-white/5 animate-fade-in">
                <div className="max-w-6xl mx-auto px-4 py-4 flex flex-col gap-2">
                  <a href={WEBSITE_URL} target="_blank" rel="noopener noreferrer"
                    className="px-4 py-3 rounded-2xl text-sm text-text-muted hover:text-white hover:bg-white/5 transition-colors">
                    Website
                  </a>
                  <a href={SUPPORT_URL} target="_blank" rel="noopener noreferrer"
                    className="px-4 py-3 rounded-2xl text-sm text-text-muted hover:text-white hover:bg-white/5 transition-colors">
                    Support
                  </a>
                  <a href={DOCS_URL} target="_blank" rel="noopener noreferrer"
                    className="px-4 py-3 rounded-2xl text-sm text-text-muted hover:text-white hover:bg-white/5 transition-colors">
                    Docs
                  </a>
                  <button onClick={() => { setShowInfo(true); setMobileMenuOpen(false); }}
                    className="px-4 py-3 rounded-2xl text-sm text-text-muted hover:text-white hover:bg-white/5 transition-colors text-left">
                    Info
                  </button>
                </div>
              </div>
            )}
          </header>

          {/* Hero Section — Centered, dramatic */}
          <section className="max-w-6xl mx-auto px-4 sm:px-6 pt-16 md:pt-24 pb-16 text-center">
            <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-accent/10 border border-accent/20 text-sm text-accent mb-8">
              Managed SaaS — No Code Required
            </span>

            <h1 className="font-display text-4xl md:text-6xl lg:text-7xl font-bold text-white mb-6 leading-tight">
              Blockchain{' '}
              <span className="bg-gradient-to-r from-accent via-emerald-400 to-teal-300 bg-clip-text text-transparent">
                Subscriptions
              </span>
              <br />
              for Your Discord
            </h1>

            <p className="text-text-secondary text-lg md:text-xl max-w-2xl mx-auto mb-10">
              Map on-chain subscriptions to Discord roles automatically. Price plans in{' '}
              <strong className="text-accent">SUBS</strong> or{' '}
              <strong className="text-accent">USDC</strong> — users always pay in SUBS,
              converted at checkout in real time.
            </p>

            <div className="flex flex-wrap items-center justify-center gap-4 mb-8">
              <a
                href={BOT_INVITE_URL}
                className="inline-flex items-center px-8 py-3 rounded-full bg-accent text-slate-950 font-bold font-display shadow-glow hover:shadow-glow-lg hover:bg-accent-hover transition-all text-lg"
              >
                + Add Subscrypts to Discord
              </a>
              <a
                href="#steps"
                className="inline-flex items-center px-6 py-3 rounded-full bg-white/5 border border-white/10 text-white hover:bg-white/10 transition-all font-medium"
              >
                See how it works
              </a>
            </div>

            <div className="flex flex-wrap items-center justify-center gap-3">
              <span className="inline-flex items-center px-3 py-1 text-xs rounded-full border border-white/10 bg-white/5 text-text-muted">
                Chain: Arbitrum One
              </span>
              <span className="inline-flex items-center px-3 py-1 text-xs rounded-full border border-white/10 bg-white/5 text-text-muted">
                Non-Custodial
              </span>
            </div>
          </section>

          <div className="max-w-6xl mx-auto px-4 sm:px-6">
            <div className="h-px bg-gradient-to-r from-transparent via-white/10 to-transparent mb-16" />
          </div>

          {/* Prerequisites Section */}
          <section className="max-w-6xl mx-auto px-4 sm:px-6 mb-16">
            <h2 className="font-display text-2xl md:text-3xl font-bold text-white mb-2">Prerequisites</h2>
            <p className="text-text-muted mb-8">Simple prep so setup is smooth.</p>

            <div className="grid md:grid-cols-3 gap-4 mb-6">
              <div className="glass-panel rounded-2xl p-6 hover:border-accent/20 transition-colors">
                <h3 className="font-display font-bold text-white mb-2">Discord Server</h3>
                <p className="text-sm text-text-muted">
                  You need to be the Discord Admin or have "Manage Server" privileges on the target server.
                </p>
              </div>
              <div className="glass-panel rounded-2xl p-6 hover:border-accent/20 transition-colors">
                <h3 className="font-display font-bold text-white mb-2">Crypto Wallet Extension</h3>
                <p className="text-sm text-text-muted">
                  <a href="https://metamask.io/download" target="_blank" rel="noopener noreferrer" className="text-accent hover:underline">
                    Install MetaMask
                  </a>{' '}
                  or any Ethereum-compatible wallet and add{' '}
                  <button onClick={handleAddArbitrum} className="text-accent hover:underline">
                    Arbitrum One
                  </button>{' '}
                  network.
                </p>
              </div>
              <div className="glass-panel rounded-2xl p-6 hover:border-accent/20 transition-colors">
                <h3 className="font-display font-bold text-white mb-2">Fund Wallet with ETH</h3>
                <p className="text-sm text-text-muted">
                  Fund your wallet with ETH for paying transactions on Arbitrum. Estimated cost per subscription: ~$0.01.
                </p>
              </div>
            </div>

            <div className="border-l-4 border-accent bg-accent/5 rounded-2xl p-4 text-sm text-accent/80">
              Tip: After configuring the Subscrypts integration in Discord, members will find a{' '}
              <strong>#swap</strong> channel with a link to swap their funds for SUBS — or they can use Uniswap directly.
            </div>
          </section>

          <div className="max-w-6xl mx-auto px-4 sm:px-6">
            <div className="h-px bg-gradient-to-r from-transparent via-white/10 to-transparent mb-16" />
          </div>

          {/* Steps Section — Individual glass cards in 2-column grid */}
          <section id="steps" className="max-w-6xl mx-auto px-4 sm:px-6 mb-16">
            <h2 className="font-display text-2xl md:text-3xl font-bold text-white mb-2">Setup — Step by Step</h2>
            <p className="text-text-muted mb-10">Follow these steps to get your server live.</p>

            <div className="space-y-8">
              {STEPS.map((step, i) => (
                <div key={step.number} className="glass-panel rounded-2xl overflow-hidden">
                  <div className={`grid md:grid-cols-2 gap-0 ${i % 2 !== 0 ? 'md:[direction:rtl]' : ''}`}>
                    {/* Text side */}
                    <div className={`p-6 md:p-8 ${i % 2 !== 0 ? 'md:[direction:ltr]' : ''}`}>
                      <div className="flex items-center gap-3 mb-4">
                        <span className="w-8 h-8 rounded-full bg-accent/10 border border-accent/20 flex items-center justify-center text-accent font-bold text-sm font-display">
                          {step.number}
                        </span>
                        <h3 className="font-display text-xl font-bold text-white">{step.title}</h3>
                      </div>
                      <p className="text-text-muted mb-4">{step.description}</p>
                      <ul className="text-sm text-text-muted space-y-1 list-disc list-inside">
                        {step.bullets.map((bullet, j) => (
                          <li key={j}>{bullet}</li>
                        ))}
                      </ul>
                    </div>
                    {/* Video side */}
                    <div className={`bg-dark-surface/50 min-h-[220px] flex items-center justify-center p-6 ${i % 2 !== 0 ? 'md:[direction:ltr]' : ''}`}>
                      <video
                        src={step.video}
                        playsInline
                        muted
                        controls
                        preload="metadata"
                        className="w-3/4 rounded-lg aspect-video"
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>

          <div className="max-w-6xl mx-auto px-4 sm:px-6">
            <div className="h-px bg-gradient-to-r from-transparent via-white/10 to-transparent mb-16" />
          </div>

          {/* Quick Help Section */}
          <section className="max-w-6xl mx-auto px-4 sm:px-6 mb-16">
            <h2 className="font-display text-2xl md:text-3xl font-bold text-white mb-8">Quick Help</h2>
            <div className="grid md:grid-cols-3 gap-4">
              <div className="glass-panel rounded-2xl p-6 hover:border-accent/20 transition-colors">
                <h3 className="font-display font-bold text-white mb-2">Role didn't apply?</h3>
                <p className="text-sm text-text-muted">
                  Bot needs <em>Manage Roles</em>, and its role must sit <strong>above</strong> premium roles.
                </p>
              </div>
              <div className="glass-panel rounded-2xl p-6 hover:border-accent/20 transition-colors">
                <h3 className="font-display font-bold text-white mb-2">Wrong network?</h3>
                <p className="text-sm text-text-muted">
                  Switch MetaMask to <strong>Arbitrum One</strong> (Chain ID 42161).
                </p>
              </div>
              <div className="glass-panel rounded-2xl p-6 hover:border-accent/20 transition-colors">
                <h3 className="font-display font-bold text-white mb-2">Out of gas?</h3>
                <p className="text-sm text-text-muted">
                  Add a little <strong>ETH on Arbitrum</strong>. Payments still settle in <strong>SUBS</strong>.
                </p>
              </div>
            </div>
          </section>

          <div className="max-w-6xl mx-auto px-4 sm:px-6">
            <div className="h-px bg-gradient-to-r from-transparent via-white/10 to-transparent mb-16" />
          </div>

          {/* FAQ Section with animated accordion */}
          <section className="max-w-6xl mx-auto px-4 sm:px-6 mb-16">
            <h2 className="font-display text-2xl md:text-3xl font-bold text-white mb-2">Frequently Asked Questions</h2>
            <p className="text-text-muted mb-8">Common questions about the Subscrypts Discord Bot.</p>

            <div className="space-y-3">
              {FAQ_DATA.map((faq, i) => (
                <div
                  key={i}
                  className="glass-panel rounded-2xl overflow-hidden"
                >
                  <button
                    onClick={() => setOpenFaq(openFaq === i ? null : i)}
                    className="w-full flex items-center justify-between p-4 md:p-5 text-left hover:bg-white/5 transition-colors"
                  >
                    <span className="font-bold text-white pr-4">{faq.question}</span>
                    <span
                      className={`text-text-muted text-xl shrink-0 transition-transform duration-300 ${
                        openFaq === i ? 'rotate-45' : ''
                      }`}
                    >
                      +
                    </span>
                  </button>
                  <div
                    className={`grid transition-[grid-template-rows] duration-300 ease-out ${
                      openFaq === i ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'
                    }`}
                  >
                    <div className="overflow-hidden min-h-0">
                      <div className="px-4 md:px-5 pb-4 md:pb-5 text-sm text-text-muted leading-relaxed animate-accordion-open">
                        {faq.answer}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* FAQ JSON-LD Structured Data */}
          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{
              __html: JSON.stringify({
                '@context': 'https://schema.org',
                '@type': 'FAQPage',
                mainEntity: FAQ_DATA.map((faq) => ({
                  '@type': 'Question',
                  name: faq.question,
                  acceptedAnswer: {
                    '@type': 'Answer',
                    text: faq.answer,
                  },
                })),
              }),
            }}
          />

          {/* Footer */}
          <footer className="max-w-6xl mx-auto px-4 sm:px-6 pb-8">
            <div className="flex flex-wrap items-center justify-between gap-4 pt-6 border-t border-white/5 text-sm text-text-muted">
              <span>&copy; {new Date().getFullYear()} Subscrypts - Discord Bot</span>
              <div className="flex items-center gap-4">
                <a href={SUPPORT_URL} target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors">
                  Support
                </a>
                <a href={DOCS_URL} target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors">
                  Docs
                </a>
                <a
                  href={BOT_INVITE_URL}
                  className="px-4 py-1.5 rounded-full bg-accent text-slate-950 font-medium hover:bg-accent-hover shadow-glow transition-all"
                >
                  Add to Discord
                </a>
              </div>
            </div>
          </footer>
        </div>

        {/* Info Sidebar */}
        <InfoSidebar
          isOpen={showInfo}
          onClose={() => setShowInfo(false)}
          {...INFO_CONTENT.home}
        />
      </main>
    </Layout>
  );
};

export default Home;
